<template>
  <div class="coupon-box-section">
    <a-divider orientation="left">쿠폰함 리소스 설정</a-divider>

    <a-alert
      message="쿠폰함 리소스 설정 가이드"
      type="info"
      show-icon
      style="margin-bottom: 24px"
    >
      <template #description>
        <ul style="margin: 8px 0 0; padding-left: 20px;">
          <li>환자용과 친구용으로 구분하여 배너/팝업 이미지를 설정합니다.</li>
          <li>권장 이미지 크기 - 배너: 700x140px, 팝업: 600x800px</li>
          <li>클릭 시 연결할 링크를 설정할 수 있습니다.</li>
        </ul>
      </template>
    </a-alert>

    <!-- 리소스별 설정 -->
    <a-row :gutter="[24, 24]">
      <!-- 환자용 배너 -->
      <a-col :span="12">
        <div class="resource-uploader">
          <h4>환자용 배너 이미지</h4>
          <p class="description">환자 쿠폰함 목록에 표시될 배너</p>

          <!-- 이미지 업로드 섹션 -->
          <div class="upload-section">
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="getResource('PATIENT_BANNER')?.url" class="image-preview-container">
              <img :src="getResource('PATIENT_BANNER').url" alt="환자용 배너" class="preview-image" />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button size="small" type="text" danger @click="handleRemove('PATIENT_BANNER')">
                  <i class="fa fa-trash"></i> 이미지 삭제
                </a-button>
                <a-button size="small" @click="openResourceModal('PATIENT_BANNER')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="uploadModes['PATIENT_BANNER']" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button size="small" @click="openResourceModal('PATIENT_BANNER')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="uploadModes['PATIENT_BANNER'] === 'url'">
                <a-input
                  :value="getResource('PATIENT_BANNER')?.url || ''"
                  placeholder="https://example.com/patient-banner.png"
                  @change="(e) => handleUrlChange(e.target.value, 'PATIENT_BANNER')"
                />
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="(file) => {
                    handleUpload(file, 'PATIENT_BANNER');
                    return false;
                  }"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="uploadingStates['PATIENT_BANNER']">
                    <i v-if="!uploadingStates['PATIENT_BANNER']" class="fa fa-cloud-upload"></i>
                    {{ uploadingStates['PATIENT_BANNER'] ? '업로드 중...' : '이미지 선택' }}
                  </a-button>
                </a-upload>
              </div>
            </div>
          </div>

          <!-- 클릭 액션 설정 섹션 -->
          <div class="click-action-section">
            <a-divider style="margin: 12px 0; font-size: 13px;">클릭 액션 설정</a-divider>

            <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
              <a-select
                :value="getResource('PATIENT_BANNER')?.clickAction?.actionType || 'none'"
                style="width: 100%;"
                @change="(value) => handleClickActionTypeChange(value, 'PATIENT_BANNER')"
              >
                <a-select-option value="none">클릭 동작 없음</a-select-option>
                <a-select-option value="link">링크로 이동</a-select-option>
              </a-select>
            </a-form-item>

            <!-- 링크 설정 -->
            <div v-if="getResource('PATIENT_BANNER')?.clickAction?.actionType === 'link'">
              <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                <a-input
                  :value="getResource('PATIENT_BANNER')?.clickAction?.url || ''"
                  placeholder="https://example.com 또는 /internal/path"
                  @change="(e) => handleClickActionUrlChange(e.target.value, 'PATIENT_BANNER')"
                />
              </a-form-item>

              <a-form-item label="열기 방식" style="margin-bottom: 8px;">
                <a-radio-group
                  :value="getResource('PATIENT_BANNER')?.clickAction?.target || '_blank'"
                  @change="(e) => handleClickActionTargetChange(e.target.value, 'PATIENT_BANNER')"
                >
                  <a-radio value="_blank">새 창</a-radio>
                  <a-radio value="_self">같은 창</a-radio>
                </a-radio-group>
              </a-form-item>
            </div>
          </div>
        </div>
      </a-col>

      <!-- 환자용 팝업 -->
      <a-col :span="12">
        <div class="resource-uploader">
          <h4>환자용 팝업 이미지</h4>
          <p class="description">환자 쿠폰 클릭 시 표시될 팝업</p>

          <!-- 이미지 업로드 섹션 -->
          <div class="upload-section">
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="getResource('PATIENT_POPUP')?.url" class="image-preview-container">
              <img :src="getResource('PATIENT_POPUP').url" alt="환자용 팝업" class="preview-image" />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button size="small" type="text" danger @click="handleRemove('PATIENT_POPUP')">
                  <i class="fa fa-trash"></i> 이미지 삭제
                </a-button>
                <a-button size="small" @click="openResourceModal('PATIENT_POPUP')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="uploadModes['PATIENT_POPUP']" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button size="small" @click="openResourceModal('PATIENT_POPUP')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="uploadModes['PATIENT_POPUP'] === 'url'">
                <a-input
                  :value="getResource('PATIENT_POPUP')?.url || ''"
                  placeholder="https://example.com/patient-popup.png"
                  @change="(e) => handleUrlChange(e.target.value, 'PATIENT_POPUP')"
                />
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="(file) => {
                    handleUpload(file, 'PATIENT_POPUP');
                    return false;
                  }"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="uploadingStates['PATIENT_POPUP']">
                    <i v-if="!uploadingStates['PATIENT_POPUP']" class="fa fa-cloud-upload"></i>
                    {{ uploadingStates['PATIENT_POPUP'] ? '업로드 중...' : '이미지 선택' }}
                  </a-button>
                </a-upload>
              </div>
            </div>
          </div>

          <!-- 클릭 액션 설정 섹션 -->
          <div class="click-action-section">
            <a-divider style="margin: 12px 0; font-size: 13px;">클릭 액션 설정</a-divider>

            <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
              <a-select
                :value="getResource('PATIENT_POPUP')?.clickAction?.actionType || 'none'"
                style="width: 100%;"
                @change="(value) => handleClickActionTypeChange(value, 'PATIENT_POPUP')"
              >
                <a-select-option value="none">클릭 동작 없음</a-select-option>
                <a-select-option value="link">링크로 이동</a-select-option>
              </a-select>
            </a-form-item>

            <!-- 링크 설정 -->
            <div v-if="getResource('PATIENT_POPUP')?.clickAction?.actionType === 'link'">
              <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                <a-input
                  :value="getResource('PATIENT_POPUP')?.clickAction?.url || ''"
                  placeholder="https://example.com 또는 /internal/path"
                  @change="(e) => handleClickActionUrlChange(e.target.value, 'PATIENT_POPUP')"
                />
              </a-form-item>

              <a-form-item label="열기 방식" style="margin-bottom: 8px;">
                <a-radio-group
                  :value="getResource('PATIENT_POPUP')?.clickAction?.target || '_blank'"
                  @change="(e) => handleClickActionTargetChange(e.target.value, 'PATIENT_POPUP')"
                >
                  <a-radio value="_blank">새 창</a-radio>
                  <a-radio value="_self">같은 창</a-radio>
                </a-radio-group>
              </a-form-item>
            </div>
          </div>
        </div>
      </a-col>

      <!-- 친구용 배너 -->
      <a-col :span="12">
        <div class="resource-uploader">
          <h4>친구용 배너 이미지</h4>
          <p class="description">친구 쿠폰함 목록에 표시될 배너</p>

          <!-- 이미지 업로드 섹션 -->
          <div class="upload-section">
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="getResource('FRIEND_BANNER')?.url" class="image-preview-container">
              <img :src="getResource('FRIEND_BANNER').url" alt="친구용 배너" class="preview-image" />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button size="small" type="text" danger @click="handleRemove('FRIEND_BANNER')">
                  <i class="fa fa-trash"></i> 이미지 삭제
                </a-button>
                <a-button size="small" @click="openResourceModal('FRIEND_BANNER')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="uploadModes['FRIEND_BANNER']" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button size="small" @click="openResourceModal('FRIEND_BANNER')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="uploadModes['FRIEND_BANNER'] === 'url'">
                <a-input
                  :value="getResource('FRIEND_BANNER')?.url || ''"
                  placeholder="https://example.com/friend-banner.png"
                  @change="(e) => handleUrlChange(e.target.value, 'FRIEND_BANNER')"
                />
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="(file) => {
                    handleUpload(file, 'FRIEND_BANNER');
                    return false;
                  }"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="uploadingStates['FRIEND_BANNER']">
                    <i v-if="!uploadingStates['FRIEND_BANNER']" class="fa fa-cloud-upload"></i>
                    {{ uploadingStates['FRIEND_BANNER'] ? '업로드 중...' : '이미지 선택' }}
                  </a-button>
                </a-upload>
              </div>
            </div>
          </div>

          <!-- 클릭 액션 설정 섹션 -->
          <div class="click-action-section">
            <a-divider style="margin: 12px 0; font-size: 13px;">클릭 액션 설정</a-divider>

            <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
              <a-select
                :value="getResource('FRIEND_BANNER')?.clickAction?.actionType || 'none'"
                style="width: 100%;"
                @change="(value) => handleClickActionTypeChange(value, 'FRIEND_BANNER')"
              >
                <a-select-option value="none">클릭 동작 없음</a-select-option>
                <a-select-option value="link">링크로 이동</a-select-option>
              </a-select>
            </a-form-item>

            <!-- 링크 설정 -->
            <div v-if="getResource('FRIEND_BANNER')?.clickAction?.actionType === 'link'">
              <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                <a-input
                  :value="getResource('FRIEND_BANNER')?.clickAction?.url || ''"
                  placeholder="https://example.com 또는 /internal/path"
                  @change="(e) => handleClickActionUrlChange(e.target.value, 'FRIEND_BANNER')"
                />
              </a-form-item>

              <a-form-item label="열기 방식" style="margin-bottom: 8px;">
                <a-radio-group
                  :value="getResource('FRIEND_BANNER')?.clickAction?.target || '_blank'"
                  @change="(e) => handleClickActionTargetChange(e.target.value, 'FRIEND_BANNER')"
                >
                  <a-radio value="_blank">새 창</a-radio>
                  <a-radio value="_self">같은 창</a-radio>
                </a-radio-group>
              </a-form-item>
            </div>
          </div>
        </div>
      </a-col>

      <!-- 친구용 팝업 -->
      <a-col :span="12">
        <div class="resource-uploader">
          <h4>친구용 팝업 이미지</h4>
          <p class="description">친구 쿠폰 클릭 시 표시될 팝업</p>

          <!-- 이미지 업로드 섹션 -->
          <div class="upload-section">
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="getResource('FRIEND_POPUP')?.url" class="image-preview-container">
              <img :src="getResource('FRIEND_POPUP').url" alt="친구용 팝업" class="preview-image" />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button size="small" type="text" danger @click="handleRemove('FRIEND_POPUP')">
                  <i class="fa fa-trash"></i> 이미지 삭제
                </a-button>
                <a-button size="small" @click="openResourceModal('FRIEND_POPUP')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="uploadModes['FRIEND_POPUP']" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button size="small" @click="openResourceModal('FRIEND_POPUP')">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="uploadModes['FRIEND_POPUP'] === 'url'">
                <a-input
                  :value="getResource('FRIEND_POPUP')?.url || ''"
                  placeholder="https://example.com/friend-popup.png"
                  @change="(e) => handleUrlChange(e.target.value, 'FRIEND_POPUP')"
                />
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="(file) => {
                    handleUpload(file, 'FRIEND_POPUP');
                    return false;
                  }"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="uploadingStates['FRIEND_POPUP']">
                    <i v-if="!uploadingStates['FRIEND_POPUP']" class="fa fa-cloud-upload"></i>
                    {{ uploadingStates['FRIEND_POPUP'] ? '업로드 중...' : '이미지 선택' }}
                  </a-button>
                </a-upload>
              </div>
            </div>
          </div>

          <!-- 클릭 액션 설정 섹션 -->
          <div class="click-action-section">
            <a-divider style="margin: 12px 0; font-size: 13px;">클릭 액션 설정</a-divider>

            <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
              <a-select
                :value="getResource('FRIEND_POPUP')?.clickAction?.actionType || 'none'"
                style="width: 100%;"
                @change="(value) => handleClickActionTypeChange(value, 'FRIEND_POPUP')"
              >
                <a-select-option value="none">클릭 동작 없음</a-select-option>
                <a-select-option value="link">링크로 이동</a-select-option>
              </a-select>
            </a-form-item>

            <!-- 링크 설정 -->
            <div v-if="getResource('FRIEND_POPUP')?.clickAction?.actionType === 'link'">
              <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                <a-input
                  :value="getResource('FRIEND_POPUP')?.clickAction?.url || ''"
                  placeholder="https://example.com 또는 /internal/path"
                  @change="(e) => handleClickActionUrlChange(e.target.value, 'FRIEND_POPUP')"
                />
              </a-form-item>

              <a-form-item label="열기 방식" style="margin-bottom: 8px;">
                <a-radio-group
                  :value="getResource('FRIEND_POPUP')?.clickAction?.target || '_blank'"
                  @change="(e) => handleClickActionTargetChange(e.target.value, 'FRIEND_POPUP')"
                >
                  <a-radio value="_blank">새 창</a-radio>
                  <a-radio value="_self">같은 창</a-radio>
                </a-radio-group>
              </a-form-item>
            </div>
          </div>
        </div>
      </a-col>
    </a-row>

    <!-- 이탈방지 팝업 설정 추가 -->
    <a-divider orientation="left" style="margin-top: 48px;">이탈방지 팝업 설정</a-divider>

    <a-alert
      message="이탈방지 팝업 설정 가이드"
      type="info"
      show-icon
      style="margin-bottom: 24px"
    >
      <template #description>
        <ul style="margin: 8px 0 0; padding-left: 20px;">
          <li>사용자가 페이지를 벗어나려 할 때 표시되는 팝업을 설정합니다.</li>
          <li>권장 이미지 크기: 600x400px</li>
          <li>팝업 클릭 시 이동할 링크를 설정할 수 있습니다.</li>
          <li>버튼 이미지를 따로 지정할 경우 버튼에 대한 호버 이펙트가 추가되고 버튼 클릭시 이동할 링크를 설정할 수 있습니다.</li>
          <li>버튼 클릭시 이동할 동작을 선택하지 않을경우 버튼 클릭시 팝업이 닫힙니다.</li>
        </ul>
      </template>
    </a-alert>

    <a-row :gutter="[24, 24]">
      <a-col :span="24">
        <div class="exit-popup-settings">
          <!-- 이미지 업로드 섹션 -->
          <a-row :gutter="24">
            <a-col :span="12">
              <div class="setting-group">
                <h4>이탈방지 팝업 이미지</h4>

                <!-- 이미지가 있을 경우 미리보기 -->
                <div v-if="form.exitPreventionPopup.imageUrl" class="image-preview-container">
                  <img :src="form.exitPreventionPopup.imageUrl" alt="이탈방지 팝업" class="preview-image" />
                  <div style="margin-top: 8px; display: flex; gap: 8px;">
                    <a-button size="small" type="text" danger @click="handleExitPopupRemoveImage">
                      <i class="fa fa-trash"></i> 이미지 삭제
                    </a-button>
                    <a-button size="small" @click="openResourceModal('EXIT_POPUP')">
                      <template #icon><i class="fa fa-folder-open"></i></template>
                      자료실에서 선택
                    </a-button>
                  </div>
                </div>

                <!-- 이미지가 없을 경우 업로드 옵션 -->
                <div v-else>
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                    <a-radio-group v-model:value="exitPopupUploadMode" size="small">
                      <a-radio-button value="url">URL 입력</a-radio-button>
                      <a-radio-button value="upload">파일 업로드</a-radio-button>
                    </a-radio-group>
                    <a-button size="small" @click="openResourceModal('EXIT_POPUP')">
                      <template #icon><i class="fa fa-folder-open"></i></template>
                      자료실에서 선택
                    </a-button>
                  </div>

                  <!-- URL 입력 모드 -->
                  <div v-if="exitPopupUploadMode === 'url'">
                    <a-input
                      v-model:value="form.exitPreventionPopup.imageUrl"
                      placeholder="https://example.com/exit-popup.png"
                    />
                  </div>

                  <!-- 파일 업로드 모드 -->
                  <div v-else>
                    <a-upload
                      :before-upload="handleExitPopupUpload"
                      :show-upload-list="false"
                      accept="image/*"
                    >
                      <a-button :loading="exitPopupUploading">
                        <i v-if="!exitPopupUploading" class="fa fa-cloud-upload"></i>
                        {{ exitPopupUploading ? '업로드 중...' : '이미지 선택' }}
                      </a-button>
                    </a-upload>
                  </div>
                </div>
              </div>
            </a-col>

            <a-col :span="12">
              <div class="setting-group">
                <h4 style="margin-bottom:25px;">팝업 클릭 액션 설정</h4>

                <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
                  <a-select
                    v-model:value="form.exitPreventionPopup.clickAction.actionType"
                    style="width: 100%;"
                  >
                    <a-select-option value="none">클릭 동작 없음</a-select-option>
                    <a-select-option value="link">링크로 이동</a-select-option>
                  </a-select>
                </a-form-item>

                <!-- 링크 설정 -->
                <div v-if="form.exitPreventionPopup.clickAction.actionType === 'link'">
                  <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                    <a-input
                      v-model:value="form.exitPreventionPopup.clickAction.url"
                      placeholder="https://example.com 또는 /internal/path"
                    />
                  </a-form-item>

                  <a-form-item label="열기 방식">
                    <a-radio-group v-model:value="form.exitPreventionPopup.clickAction.target">
                      <a-radio value="_blank">새 창으로 열기</a-radio>
                      <a-radio value="_self">같은 창에서 열기</a-radio>
                    </a-radio-group>
                  </a-form-item>
                </div>

                <a-form-item>
                  <a-checkbox v-model:checked="form.exitPreventionPopup.enabled">
                    이탈방지 팝업 사용
                  </a-checkbox>
                </a-form-item>
              </div>
            </a-col>
          </a-row>
        </div>
      </a-col>
    </a-row>
    <a-row :gutter="[24, 24]">
      <a-col :span="24">
        <div class="exit-popup-settings">
          <!-- 버튼 이미지 업로드 섹션 -->
          <a-row :gutter="24">
            <a-col :span="12">
              <div class="setting-group">
                <h4>버튼 이미지</h4>

                <!-- 이미지가 있을 경우 미리보기 -->
                <div v-if="form.exitPreventionPopup.buttonImageUrl" class="image-preview-container">
                  <img :src="form.exitPreventionPopup.buttonImageUrl" alt="버튼 이미지지" class="preview-image" />
                  <div style="margin-top: 8px; display: flex; gap: 8px;">
                    <a-button size="small" type="text" danger @click="handleExitPopupRemoveButtonImage">
                      <i class="fa fa-trash"></i> 이미지 삭제
                    </a-button>
                    <a-button size="small" @click="openResourceModal('EXIT_BUTTON')">
                      <template #icon><i class="fa fa-folder-open"></i></template>
                      자료실에서 선택
                    </a-button>
                  </div>
                </div>

                <!-- 이미지가 없을 경우 업로드 옵션 -->
                <div v-else>
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                    <a-radio-group v-model:value="exitPopupButtonUploadMode" size="small">
                      <a-radio-button value="url">URL 입력</a-radio-button>
                      <a-radio-button value="upload">파일 업로드</a-radio-button>
                    </a-radio-group>
                    <a-button size="small" @click="openResourceModal('EXIT_BUTTON')">
                      <template #icon><i class="fa fa-folder-open"></i></template>
                      자료실에서 선택
                    </a-button>
                  </div>

                  <!-- URL 입력 모드 -->
                  <div v-if="exitPopupButtonUploadMode === 'url'">
                    <a-input
                      v-model:value="form.exitPreventionPopup.buttonImageUrl"
                      placeholder="https://example.com/exit-popup.png"
                    />
                  </div>

                  <!-- 파일 업로드 모드 -->
                  <div v-else>
                    <a-upload
                      :before-upload="handleExitPopupButtonUpload"
                      :show-upload-list="false"
                      accept="image/*"
                    >
                      <a-button :loading="exitPopupButtonUploading">
                        <i v-if="!exitPopupButtonUploading" class="fa fa-cloud-upload"></i>
                        {{ exitPopupButtonUploading ? '업로드 중...' : '이미지 선택' }}
                      </a-button>
                    </a-upload>
                  </div>
                </div>
              </div>
            </a-col>

            <a-col :span="12">
              <div class="setting-group">
                <h4 style="margin-bottom:25px;">버튼 클릭 액션 설정</h4>

                <a-form-item label="클릭 동작" style="margin-bottom: 12px;">
                  <a-select
                    :value="form.exitPreventionPopup.buttonClickAction?.actionType || 'none'"
                    style="width: 100%;"
                    @change="(value) => {
                      if (!form.exitPreventionPopup.buttonClickAction) {
                        form.exitPreventionPopup.buttonClickAction = {};
                      }
                      form.exitPreventionPopup.buttonClickAction.actionType = value;
                    }"
                  >
                    <a-select-option value="none">클릭 동작 없음</a-select-option>
                    <a-select-option value="link">링크로 이동</a-select-option>
                  </a-select>
                </a-form-item>

                <!-- 링크 설정 -->
                <div v-if="form.exitPreventionPopup.buttonClickAction?.actionType === 'link'">
                  <a-form-item label="연결 URL" style="margin-bottom: 12px;">
                    <a-input
                      :value="form.exitPreventionPopup.buttonClickAction?.url || ''"
                      placeholder="https://example.com 또는 /internal/path"
                      @change="(e) => {
                        if (!form.exitPreventionPopup.buttonClickAction) {
                          form.exitPreventionPopup.buttonClickAction = {};
                        }
                        form.exitPreventionPopup.buttonClickAction.url = e.target.value;
                      }"
                    />
                  </a-form-item>

                  <a-form-item label="열기 방식">
                    <a-radio-group
                      :value="form.exitPreventionPopup.buttonClickAction?.target || '_blank'"
                      @change="(e) => {
                        if (!form.exitPreventionPopup.buttonClickAction) {
                          form.exitPreventionPopup.buttonClickAction = {};
                        }
                        form.exitPreventionPopup.buttonClickAction.target = e.target.value;
                      }"
                    >
                      <a-radio value="_blank">새 창으로 열기</a-radio>
                      <a-radio value="_self">같은 창에서 열기</a-radio>
                    </a-radio-group>
                  </a-form-item>
                </div>
              </div>
            </a-col>
          </a-row>
        </div>
      </a-col>
    </a-row>

    <!-- 자료실 모달 -->
    <EventResourceModal
      :open="showResourceModal"
      :api="eventApi"
      @select="handleFileSelect"
      @cancel="showResourceModal = false"
    />
  </div>
</template>

<script>
import { reactive, ref, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import EventResourceModal from '../EventResourceModal.vue';
import eventsApis from '@/views/Events/api';

export default {
  name: 'BannerPopupSection',
  components: {
    EventResourceModal
  },
  props: {
    form: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { proxy: app } = getCurrentInstance();

    // API 인스턴스 생성
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;
    const store = instance?.proxy?.$store;
    const eventApi = eventsApis(apiInstance, store);

    // 자료실 모달 관련
    const showResourceModal = ref(false);
    const selectingField = ref(''); // 'PATIENT_BANNER', 'PATIENT_POPUP', etc.

    const uploadingStates = reactive({
      'PATIENT_BANNER': false,
      'PATIENT_POPUP': false,
      'FRIEND_BANNER': false,
      'FRIEND_POPUP': false
    });

    const uploadModes = reactive({
      'PATIENT_BANNER': 'url',
      'PATIENT_POPUP': 'url',
      'FRIEND_BANNER': 'url',
      'FRIEND_POPUP': 'url'
    });

    // 이탈방지 팝업 설정 관련 상태
    const exitPopupUploadMode = ref('url');
    const exitPopupUploading = ref(false);
    
    // 버튼 이미지 설정 관련 상태
    const exitPopupButtonUploadMode = ref('url');
    const exitPopupButtonUploading = ref(false);

    // 이탈방지 팝업 설정 데이터 초기화 (백엔드 스펙에 맞춤)
    if (!props.form.exitPreventionPopup) {
      props.form.exitPreventionPopup = {
        enabled: false,
        imageUrl: '',
        buttonImageUrl: '',
        clickAction: {
          actionType: 'none',
          url: '',
          target: '_blank'
        },
        buttonClickAction: {
          actionType: 'none',
          url: '',
          target: '_blank'
        }
      };
    } else {
      // 기존 데이터가 있는 경우 누락된 필드들 초기화
      if (!props.form.exitPreventionPopup.buttonImageUrl) {
        props.form.exitPreventionPopup.buttonImageUrl = '';
      }
      if (!props.form.exitPreventionPopup.buttonClickAction) {
        props.form.exitPreventionPopup.buttonClickAction = {
          actionType: 'none',
          url: '',
          target: '_blank'
        };
      }
    }

    // clickAction이 없는 경우 초기화
    if (!props.form.exitPreventionPopup.clickAction) {
      props.form.exitPreventionPopup.clickAction = {
        actionType: 'none',
        url: '',
        target: '_blank'
      };
    }

    // buttonClickAction이 없는 경우 초기화
    if (!props.form.exitPreventionPopup.buttonClickAction) {
      props.form.exitPreventionPopup.buttonClickAction = {
        actionType: 'none',
        url: '',
        target: '_blank'
      };
    }

    // 리소스 타입별로 리소스 가져오기
    const getResource = (resourceType) => {
      if (!props.form.couponBoxResources || !Array.isArray(props.form.couponBoxResources)) {
        // 리소스가 없을 경우 기본 구조 생성
        if (!props.form.couponBoxResources) {
          props.form.couponBoxResources = [
            { resourceId: 'patient-banner', resourceType: 'PATIENT_BANNER', name: '환자용 배너', url: '', isActive: true, clickAction: { actionType: 'none' } },
            { resourceId: 'patient-popup', resourceType: 'PATIENT_POPUP', name: '환자용 팝업', url: '', isActive: true, clickAction: { actionType: 'none' } },
            { resourceId: 'friend-banner', resourceType: 'FRIEND_BANNER', name: '친구용 배너', url: '', isActive: true, clickAction: { actionType: 'none' } },
            { resourceId: 'friend-popup', resourceType: 'FRIEND_POPUP', name: '친구용 팝업', url: '', isActive: true, clickAction: { actionType: 'none' } }
          ];
        }
      }

      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);

      // clickAction이 없는 리소스에 기본값 추가
      if (resource && !resource.clickAction) {
        resource.clickAction = { actionType: 'none' };
      }

      return resource;
    };

    // URL 변경 처리
    const handleUrlChange = (url, resourceType) => {
      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
      if (resource) {
        resource.url = url;
      }
    };

    // 이미지 삭제 처리
    const handleRemove = (resourceType) => {
      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
      if (resource) {
        resource.url = '';
      }
    };

    // 클릭 액션 타입 변경
    const handleClickActionTypeChange = (value, resourceType) => {
      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
      if (resource) {
        if (!resource.clickAction) {
          resource.clickAction = {};
        }
        resource.clickAction.actionType = value;
        if (value === 'none') {
          resource.clickAction.url = '';
          resource.clickAction.target = '_blank';
        } else if (value === 'link' && !resource.clickAction.target) {
          resource.clickAction.target = '_blank';
        }
      }
    };

    // 클릭 액션 URL 변경
    const handleClickActionUrlChange = (url, resourceType) => {
      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
      if (resource && resource.clickAction) {
        resource.clickAction.url = url;
      }
    };

    // 클릭 액션 타겟 변경
    const handleClickActionTargetChange = (target, resourceType) => {
      const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
      if (resource && resource.clickAction) {
        resource.clickAction.target = target;
      }
    };

    // 파일 업로드 처리
    const handleUpload = async (file, resourceType) => {
      console.log('handleUpload called:', file, resourceType);

      if (!file) {
        console.error('파일이 없습니다');
        return;
      }

      // 파일 유효성 검사
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다.');
        return;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('이미지 크기는 5MB 이하여야 합니다.');
        return;
      }

      uploadingStates[resourceType] = true;

      try {
        // FormData 생성
        const formData = new FormData();
        formData.append('container', 'certilife');
        formData.append('files', file);

        // Azure Blob Storage 업로드 API 호출
        const response = await app.$apiInstance.post(
          '/object-storages/azure/files/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Azure-Container': 'certilife'
            },
            timeout: 120000
          }
        );

        console.log('업로드 응답:', response);

        // 업로드 성공 처리 - PropertyEditor와 동일한 응답 구조
        if (response.data && response.data.success && response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          const uploadedFileUrl = response.data.data.results[0].url;
          const resource = props.form.couponBoxResources.find(r => r.resourceType === resourceType);
          if (resource) {
            resource.url = uploadedFileUrl;
            message.success('이미지가 성공적으로 업로드되었습니다.');
            console.log('업로드된 이미지 URL:', uploadedFileUrl);
          }
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
        if (error.response) {
          console.error('에러 응답:', error.response);
          message.error(`이미지 업로드 실패: ${error.response.data?.message || '알 수 없는 오류'}`);
        } else {
          message.error('이미지 업로드에 실패했습니다.');
        }
      } finally {
        uploadingStates[resourceType] = false;
      }
    };

    // 이탈방지 팝업 이미지 삭제
    const handleExitPopupRemoveImage = () => {
      props.form.exitPreventionPopup.imageUrl = '';
    };

    // 이탈방지 팝업 이미지 업로드
    const handleExitPopupUpload = async (file) => {
      console.log('handleExitPopupUpload called:', file);

      if (!file) {
        console.error('파일이 없습니다');
        return false;
      }

      // 파일 유효성 검사
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다.');
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('이미지 크기는 5MB 이하여야 합니다.');
        return false;
      }

      exitPopupUploading.value = true;

      try {
        // FormData 생성
        const formData = new FormData();
        formData.append('container', 'certilife');
        formData.append('files', file);

        // Azure Blob Storage 업로드 API 호출
        const response = await app.$apiInstance.post(
          '/object-storages/azure/files/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Azure-Container': 'certilife'
            },
            timeout: 120000
          }
        );

        console.log('이탈방지 팝업 업로드 응답:', response);

        // 업로드 성공 처리
        if (response.data && response.data.success && response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          const uploadedFileUrl = response.data.data.results[0].url;
          props.form.exitPreventionPopup.imageUrl = uploadedFileUrl;
          message.success('이탈방지 팝업 이미지가 성공적으로 업로드되었습니다.');
          console.log('업로드된 이탈방지 팝업 URL:', uploadedFileUrl);
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('이탈방지 팝업 이미지 업로드 실패:', error);
        if (error.response) {
          console.error('에러 응답:', error.response);
          message.error(`이미지 업로드 실패: ${error.response.data?.message || '알 수 없는 오류'}`);
        } else {
          message.error('이미지 업로드에 실패했습니다.');
        }
      } finally {
        exitPopupUploading.value = false;
      }

      return false; // 기본 업로드 동작 방지
    };

    // 이탈방지 팝업 버튼 이미지 삭제
    const handleExitPopupRemoveButtonImage = () => {
      props.form.exitPreventionPopup.buttonImageUrl = '';
    };

    // 이탈방지 팝업 버튼 이미지 업로드
    const handleExitPopupButtonUpload = async (file) => {
      console.log('handleExitPopupButtonUpload called:', file);

      if (!file) {
        console.error('파일이 없습니다');
        return false;
      }

      // 파일 유효성 검사
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다.');
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('이미지 크기는 5MB 이하여야 합니다.');
        return false;
      }

      exitPopupButtonUploading.value = true;

      try {
        // FormData 생성
        const formData = new FormData();
        formData.append('container', 'certilife');
        formData.append('files', file);

        // Azure Blob Storage 업로드 API 호출
        const response = await app.$apiInstance.post(
          '/object-storages/azure/files/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Azure-Container': 'certilife'
            },
            timeout: 120000
          }
        );

        console.log('버튼 이미지 업로드 응답:', response);

        // 업로드 성공 처리
        if (response.data && response.data.success && response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          const uploadedFileUrl = response.data.data.results[0].url;
          props.form.exitPreventionPopup.buttonImageUrl = uploadedFileUrl;
          message.success('버튼 이미지가 성공적으로 업로드되었습니다.');
          console.log('업로드된 버튼 이미지 URL:', uploadedFileUrl);
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('버튼 이미지 업로드 실패:', error);
        if (error.response) {
          console.error('에러 응답:', error.response);
          message.error(`이미지 업로드 실패: ${error.response.data?.message || '알 수 없는 오류'}`);
        } else {
          message.error('이미지 업로드에 실패했습니다.');
        }
      } finally {
        exitPopupButtonUploading.value = false;
      }

      return false; // 기본 업로드 동작 방지
    };

    // 자료실에서 파일 선택 핸들러
    const handleFileSelect = (file) => {
      const field = selectingField.value;

      if (field === 'PATIENT_BANNER' || field === 'PATIENT_POPUP' || field === 'FRIEND_BANNER' || field === 'FRIEND_POPUP') {
        // 쿠폰함 리소스
        const resource = props.form.couponBoxResources.find(r => r.resourceType === field);
        if (resource) {
          resource.url = file.url;
          message.success('이미지가 선택되었습니다.');
        }
      } else if (field === 'EXIT_POPUP') {
        // 이탈방지 팝업 이미지
        props.form.exitPreventionPopup.imageUrl = file.url;
        message.success('이미지가 선택되었습니다.');
      } else if (field === 'EXIT_BUTTON') {
        // 이탈방지 버튼 이미지
        props.form.exitPreventionPopup.buttonImageUrl = file.url;
        message.success('이미지가 선택되었습니다.');
      }

      showResourceModal.value = false;
    };

    // 자료실 모달 열기
    const openResourceModal = (field) => {
      selectingField.value = field;
      showResourceModal.value = true;
    };

    return {
      uploadingStates,
      uploadModes,
      getResource,
      handleUrlChange,
      handleRemove,
      handleUpload,
      handleClickActionTypeChange,
      handleClickActionUrlChange,
      handleClickActionTargetChange,
      // 이탈방지 팝업 관련
      exitPopupUploadMode,
      exitPopupUploading,
      handleExitPopupRemoveImage,
      handleExitPopupUpload,
      // 버튼 이미지 관련
      exitPopupButtonUploadMode,
      exitPopupButtonUploading,
      handleExitPopupRemoveButtonImage,
      handleExitPopupButtonUpload,
      // 자료실 모달 관련
      showResourceModal,
      eventApi,
      handleFileSelect,
      openResourceModal
    };
  }
};
</script>

<style lang="scss" scoped>
.coupon-box-section {
  padding: 24px 0;

  .resource-uploader {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;

    h4 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 500;
    }

    .description {
      margin: 0 0 12px 0;
      color: #666;
      font-size: 13px;
    }

    .upload-section {
      flex: 1;
      min-height: 120px;

      .image-preview-container {
        text-align: center;

        .preview-image {
          max-width: 100%;
          max-height: 180px;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          margin-bottom: 8px;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
      }
    }

    .click-action-section {
      border-top: 1px solid #e8e8e8;
      padding-top: 12px;
      margin-top: 16px;

      .ant-divider {
        color: #666;
        font-weight: normal;
      }

      :deep(.ant-form-item) {
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(.ant-form-item-label) {
        padding-bottom: 4px;

        > label {
          font-size: 13px;
          color: #666;
        }
      }
    }
  }

  :deep(.ant-col) {
    display: flex;

    > div {
      width: 100%;
    }
  }

  // 이탈방지 팝업 설정 스타일
  .exit-popup-settings {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 24px;

    .setting-group {
      h4 {
        margin: 0 0 16px 0;
        font-size: 15px;
        font-weight: 500;
        color: #333;
      }

      .image-preview-container {
        text-align: center;
        padding: 16px;
        background: white;
        border-radius: 4px;
        border: 1px solid #e8e8e8;

        .preview-image {
          max-width: 100%;
          max-height: 200px;
          border-radius: 4px;
          margin-bottom: 12px;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
      }

      :deep(.ant-form-item) {
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(.ant-form-item-label) {
        padding-bottom: 4px;

        > label {
          font-size: 14px;
          color: #666;
        }
      }
    }
  }
}
</style>